import { TtsError } from '../../core/tts';
import type { TtsAdapter, TtsRequest, TtsResult } from '../../core/tts';
import { type MimoClient, mimoClient } from './client';
import { displayMimoError, MimoError, TRANSFER_FAILURE } from './contracts';
import type { SynthesisInput, TtsMode, VoicePreferences, VoiceMode } from './contracts';
import { blobBytes, digest, type MimoStorage, mimoStorage, verifyAudio } from './storage';
import type { LocalAudio, LocalReference } from './storage';
import type { Preset } from '../../core/types';

const storageTails = new WeakMap<MimoStorage, Promise<unknown>>();

export interface TtsSelection { userId: string; mode: TtsMode; preferences: VoicePreferences; }
export interface LocalRoleVoice {
  description: string;
  reference?: LocalReference;
  autoVoiceDesign?: boolean;
  preset?: Preset;
  mode?: VoiceMode;
}
export interface ServerTtsAdapterOptions {
  id?: string;
  getSelection: () => TtsSelection | Promise<TtsSelection>;
  client?: MimoClient;
  storage?: MimoStorage;
  getMessageId?: (request: TtsRequest) => string | undefined;
  preview?: boolean;
  /** Per-character device data; used only for this synthesis, never saved to server preferences. */
  localVoice?: LocalRoleVoice;
  getLocalVoice?: (request: TtsRequest, selection: TtsSelection) => Promise<LocalRoleVoice | undefined>;
}
async function cacheKey(text: string, mode: VoiceMode, preferences: VoicePreferences, reference?: LocalReference, roleContext?: unknown): Promise<string> {
  return digest(JSON.stringify([text.trim(), mode, preferences[mode], mode === 'clone' ? reference?.sha256 : null, ...(roleContext ? [roleContext] : [])]));
}
async function referenceData(reference: LocalReference): Promise<string> {
  await verifyAudio(reference.blob, { bytes: reference.blob.size, sha256: reference.sha256 });
  const bytes = new Uint8Array(await blobBytes(reference.blob));
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return `data:${reference.blob.type};base64,${btoa(binary)}`;
}
export async function retryPendingAcks(userId: string, client: MimoClient = mimoClient, storage: MimoStorage = mimoStorage): Promise<number> {
  let completed = 0;
  const pending = await storage.db.pendingAcks.where('userId').equals(userId).toArray();
  if (pending.length === 0) return 0;
  try { if ((await client.account()).userId !== userId) return 0; }
  catch { return 0; }
  for (const ack of pending) {
    try {
      const local = await storage.readAudio(userId, ack.key);
      if (!local) continue;
      await verifyAudio(local.blob, ack);
      await client.acknowledge(ack.id, ack.sha256);
      await storage.db.pendingAcks.delete([userId, ack.id]);
      completed++;
    } catch { /* Keep the durable queue intact for the next explicit retry. */ }
  }
  return completed;
}
function checkAbort(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
}
export function createServerTtsAdapter(options: ServerTtsAdapterOptions): TtsAdapter {
  const client = options.client ?? mimoClient;
  const storage = options.storage ?? mimoStorage;
  // Shared adapters serialize cache checks, avoiding duplicate synthesis for one device store.
  async function speak(request: TtsRequest): Promise<TtsResult> {
    try {
      checkAbort(request.signal);
      const selection = await options.getSelection();
      if (!selection.userId) throw new MimoError('请先登录');
      if (selection.mode === 'off') throw new MimoError('语音已关闭');
      const localVoice = options.getLocalVoice ? await options.getLocalVoice(request, selection) : options.localVoice;
      checkAbort(request.signal);
      const mode = localVoice?.mode ?? selection.mode;
      const text = request.text.trim();
      if (!text) throw new MimoError('朗读文本不能为空');
      const preferences = localVoice ? {
        ...selection.preferences,
        design: { description: localVoice.description },
        clone: { instruction: localVoice.description },
      } : selection.preferences;
      const reference = mode === 'clone'
        ? localVoice ? localVoice.reference : await storage.reference(selection.userId)
        : undefined;
      if (mode === 'clone' && (!reference?.consent)) throw new MimoError('请上传参考音频并确认已获音色授权');
      const messageId = options.getMessageId?.(request);
      // Dynamic delivery varies per reply and role revision, even for identical spoken text.
      const roleContext = mode !== 'preset' && localVoice?.preset
        ? [messageId ?? request.messageId ?? 'preview', localVoice.autoVoiceDesign === true, localVoice.preset]
        : undefined;
      let key = await cacheKey(text, mode, preferences, reference, roleContext);
      async function finish(audio: LocalAudio): Promise<TtsResult> {
        if (messageId) await storage.linkAudio(selection.userId, audio.key, messageId);
        checkAbort(request.signal);
        return { audio: audio.blob, mime: audio.mime };
      }
      const cached = await storage.readAudio(selection.userId, key);
      if (cached) return await finish(cached);
      // Account preferences remain server-owned; a role's device description is a transient override.
      const account = await client.account(request.signal);
      if (account.userId !== selection.userId) throw new MimoError('请先登录');
      key = await cacheKey(text, mode, localVoice ? preferences : account.preferences, reference, roleContext);
      const updatedCache = await storage.readAudio(selection.userId, key);
      if (updatedCache) return await finish(updatedCache);
      const input: SynthesisInput = { mode, text, ...(messageId ? { messageId } : {}) };
      if (localVoice && mode !== 'preset' && localVoice.description.trim()) input.voiceDescription = localVoice.description;
      if (mode === 'clone' && reference) { input.reference = await referenceData(reference); input.consent = true; }
      if (mode !== 'preset' && localVoice?.preset) {
        input.autoVoiceDesign = localVoice.autoVoiceDesign === true;
        const { createRoleVoiceDirection } = await import('./voiceDesign');
        input.voiceDirection = await createRoleVoiceDirection({
          preset: localVoice.preset, mode, text,
          referenceWords: localVoice.description,
          autoVoiceDesign: input.autoVoiceDesign,
          ...(messageId ? { messageId } : {}),
          ...(request.signal ? { signal: request.signal } : {}),
        });
        // A model call may outlive a session change. Recheck before any paid TTS request.
        if ((await client.account(request.signal)).userId !== selection.userId) throw new MimoError('请先登录');
      }
      checkAbort(request.signal);
      const handle = await client.synthesize(input, request.signal);
      const blob = await client.download(handle.id, request.signal);
      await verifyAudio(blob, handle);
      checkAbort(request.signal);
      const saved = await storage.saveAudio({ ...handle, blob: new Blob([blob], { type: handle.mime }), userId: selection.userId, key, links: messageId ? [messageId] : [], preview: options.preview ?? !messageId, createdAt: Date.now() });
      await retryPendingAcks(selection.userId, client, storage);
      return await finish(saved);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      if (error instanceof Error && error.name === 'AbortError') throw error;
      throw new TtsError(displayMimoError(error), error instanceof MimoError && error.status ? 'http' : error instanceof MimoError && error.message !== TRANSFER_FAILURE ? 'config' : 'unknown', error instanceof MimoError ? error.status : undefined);
    }
  }
  return {
    id: options.id ?? 'mimo-server', kind: 'custom',
    async listVoices() {
      try { return (await client.plans()).voices; }
      catch (error) { throw new TtsError(displayMimoError(error), 'network'); }
    },
    speak(request) {
      const result = (storageTails.get(storage) ?? Promise.resolve()).then(() => speak(request));
      storageTails.set(storage, result.catch(() => undefined));
      return result;
    },
  };
}
